/**
 * Designed "account blocked" popup with a friendly tone and a one-click
 * mailto button that pre-fills a support request. Replaces the legacy
 * plain `alert(...)` and the older `#blockedPopup` drawer.
 *
 * Variants:
 *   - 'replace'      → blocked from vehicle attach/replace attempts
 *   - 'login'        → blocked from logging in (attempts threshold crossed)
 *   - 'adminBlock'   → admin disabled the account (or kicked it from server)
 *   - 'adminKick'    → forceLogout flag flipped by admin
 *
 * The modal is self-contained: it injects its own CSS once, mounts an
 * overlay with the icon/title/message + two buttons, and resolves when
 * the user picks either button. Cleanup removes the DOM node.
 */

import { Auth } from '../app/config.js';
import { LocalState } from '../core/localState.js';

/**
 * @typedef {'replace' | 'login' | 'adminBlock' | 'adminKick'} BlockedContext
 *
 * @typedef {Object} BlockedModalOptions
 * @property {BlockedContext} [context]    Picks the title/message/mailto copy.
 * @property {string}         [reason]     Admin-supplied reason. Shown in
 *                                         body for 'adminBlock' / 'adminKick'.
 * @property {string}         [phone]      Phone to embed in the mailto body.
 *                                         Defaults to LocalState.phone.
 */

/**
 * Show the blocked-account modal.
 * @param {BlockedModalOptions} [opts]
 * @returns {Promise<void>}
 */
export function showBlockedAccountModal(opts = {}) {
  const context = opts.context || 'login';
  const phone = opts.phone || LocalState.phone || '(לא זמין)';
  const copy = getCopy(context, opts.reason, phone);

  return new Promise((resolve) => {
    ensureStyles();

    // Don't stack — if the modal is already open, dismiss the old one first.
    document.querySelectorAll('.blocked-overlay').forEach((n) => n.remove());

    const overlay = document.createElement('div');
    overlay.className = 'blocked-overlay';
    overlay.innerHTML = `
      <div class="blocked-card" role="alertdialog" aria-modal="true" aria-labelledby="blockedAcctTitle">
        <div class="blocked-icon" aria-hidden="true">🔒</div>
        <h3 id="blockedAcctTitle" class="blocked-title">${escapeHtml(copy.title)}</h3>
        <p class="blocked-msg">${copy.bodyHtml}</p>
        <button class="blocked-primary" data-act="mailto">
          📧 פנייה לתמיכה לשחרור
        </button>
        <button class="blocked-secondary" data-act="close">סגור</button>
      </div>
    `;
    document.body.appendChild(overlay);

    const cleanup = () => { overlay.remove(); resolve(); };

    /** @type {HTMLButtonElement | null} */
    (overlay.querySelector('[data-act="mailto"]'))?.addEventListener('click', () => {
      openMailto({
        to: Auth.supportEmail,
        subject: copy.mailSubject,
        body: copy.mailBody,
      });
      cleanup();
    });
    /** @type {HTMLButtonElement | null} */
    (overlay.querySelector('[data-act="close"]'))?.addEventListener('click', cleanup);
  });
}

/**
 * @param {BlockedContext} context
 * @param {string | undefined} reason
 * @param {string} phone
 */
function getCopy(context, reason, phone) {
  const maxAttempts = Auth.maxAttemptsPerDay;
  const reasonLine = reason
    ? `<br><br><strong>סיבה:</strong> ${escapeHtml(reason)}`
    : '';

  switch (context) {
    case 'replace':
      return {
        title: 'החשבון מוגן זמנית',
        bodyHtml:
          `לבטיחות החשבון שלך, אפשרות שיוך/החלפת רכב נחסמה לאחר ` +
          `${maxAttempts} ניסיונות שגויים.` +
          `<br><br>` +
          `צוות התמיכה ישחרר את החסימה לאחר אימות קצר של זהותך — נשמח לעזור.`,
        mailSubject: 'AutoMe — בקשת שחרור מחסימה (שיוך/החלפת רכב)',
        mailBody:
          'שלום,\n\n' +
          `החשבון שלי נחסם לפעולת שיוך/החלפת רכב לאחר ${maxAttempts} ניסיונות.\n` +
          'אבקש לבדוק ולשחרר את החשבון.\n\n' +
          `מספר טלפון בחשבון: ${phone}\n\n` +
          'תודה.',
      };

    case 'login':
      return {
        title: 'החשבון מוגן זמנית',
        bodyHtml:
          `לבטיחות החשבון שלך, ההתחברות נחסמה לאחר ` +
          `${maxAttempts} ניסיונות שגויים.` +
          `<br><br>` +
          `צוות התמיכה ישחרר את החסימה לאחר אימות קצר של זהותך — נשמח לעזור.`,
        mailSubject: 'AutoMe — בקשת שחרור חסימת התחברות',
        mailBody:
          'שלום,\n\n' +
          'לא הצלחתי להתחבר לחשבון שלי באפליקציה — נראה שיש חסימה.\n' +
          'אבקש לבדוק ולשחרר את החשבון.\n\n' +
          `מספר טלפון: ${phone}\n\n` +
          'תודה.',
      };

    case 'adminKick':
      return {
        title: 'נותקת מהמערכת',
        bodyHtml:
          `החשבון שלך נותק על ידי מנהל המערכת.${reasonLine}` +
          `<br><br>` +
          `צוות התמיכה ישמח לעזור ולבדוק את הפנייה — מוזמן/ת לפנות אלינו.`,
        mailSubject: 'AutoMe — בקשת בירור על ניתוק חשבון',
        mailBody:
          'שלום,\n\n' +
          'החשבון שלי נותק על ידי מנהל המערכת.\n' +
          (reason ? `סיבה שהוצגה: ${reason}\n` : '') +
          'אבקש בירור על המקרה.\n\n' +
          `מספר טלפון: ${phone}\n\n` +
          'תודה.',
      };

    case 'adminBlock':
    default:
      return {
        title: 'החשבון מוגן זמנית',
        bodyHtml:
          `החשבון שלך נחסם על ידי מנהל המערכת.${reasonLine}` +
          `<br><br>` +
          `צוות התמיכה ישמח לעזור ולבחון את הפנייה לשחרור החשבון.`,
        mailSubject: 'AutoMe — בקשת שחרור חסימת חשבון',
        mailBody:
          'שלום,\n\n' +
          'החשבון שלי נחסם על ידי מנהל המערכת.\n' +
          (reason ? `סיבה שהוצגה: ${reason}\n` : '') +
          'אבקש לבדוק ולשחרר את החשבון.\n\n' +
          `מספר טלפון: ${phone}\n\n` +
          'תודה.',
      };
  }
}

/**
 * Open a pre-filled support email, always routed to Gmail (per product
 * decision — single, consistent inbox for support replies).
 *
 *   - Capacitor WebView (Android): native bridge `MailLauncher.openGmail`
 *                       tries the Gmail app first (package
 *                       `com.google.android.gm`); if not installed, opens
 *                       Gmail Web in the system browser.
 *   - Desktop / mobile browser (iPhone Safari, Chrome, etc): open Gmail Web
 *                       compose URL in a new tab. On iPhone with the Gmail
 *                       app installed, iOS Universal Links catches the
 *                       `mail.google.com` URL and opens directly in the
 *                       app. On Android browser, same flow.
 *
 * Gmail Web Compose URL format (documented):
 *   https://mail.google.com/mail/?view=cm&fs=1&to=...&su=...&body=...
 *
 * @param {{ to: string; subject: string; body: string }} opts
 */
function openMailto({ to, subject, body }) {
  /** @type {any} */
  const w = window;

  // Android native: dedicated bridge that prefers Gmail app, then falls
  // through to the device's default mail app, then Gmail Web.
  //
  // Status strings the bridge returns:
  //   'gmail-app-sendto' / 'gmail-app-view' — Gmail app opened
  //   'default-mail-app' — Gmail not installed; device default (Outlook etc) opened
  //   'web-fallback'     — No mail app installed at all → Gmail Web URL
  //   'failed' or ''     — bridge failed completely; JS fallback below.
  const bridgeAvailable =
    !!(w.MailLauncher && typeof w.MailLauncher.openGmail === 'function');
  console.log('[mailto] bridge available:', bridgeAvailable);
  if (bridgeAvailable) {
    try {
      const status = w.MailLauncher.openGmail(to, subject, body);
      console.log('[mailto] native openGmail status:', status);
      if (status && status !== 'failed') return;
    } catch (e) {
      console.warn('[mailto] native openGmail threw:', e);
    }
  }
  console.log('[mailto] using JS web fallback (window.open Gmail compose URL)');

  // Web fallback (browsers + iOS): open Gmail Web compose. Universal links
  // pick it up into the Gmail app if the user has it installed and signed in.
  //
  // We use the `extsrc=mailto&url=<encoded mailto:>` format because it's the
  // canonical handler Chrome registers Gmail under when you set Gmail as the
  // default mailto: handler (see chrome://settings/handlers). Gmail's
  // frontend always responds to that combo by opening the compose dialog
  // with the inner mailto fields parsed — much more reliable than
  // `?view=cm&fs=1&to=...&su=...&body=...`, which Gmail's SPA sometimes
  // ignores when the inbox is already cached.
  const innerMailto =
    `mailto:${encodeURIComponent(to)}` +
    `?subject=${encodeURIComponent(subject)}` +
    `&body=${encodeURIComponent(body)}`;
  const webUrl =
    `https://mail.google.com/mail/?extsrc=mailto&url=${encodeURIComponent(innerMailto)}`;

  const opened = w.open(webUrl, '_blank', 'noopener');
  if (opened) return;

  // Popup blocker swallowed window.open — fall back to same-tab navigation
  // via a synthetic anchor click (preserves user-gesture context).
  const a = document.createElement('a');
  a.href = webUrl;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.style.display = 'none';
  document.body.appendChild(a);
  try { a.click(); }
  finally { a.remove(); }
}

/** @param {string} s */
function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

let __stylesInjected = false;
function ensureStyles() {
  if (__stylesInjected) return;
  __stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .blocked-overlay {
      position: fixed; inset: 0;
      background: rgba(15, 23, 42, 0.65);
      backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
      z-index: 99999;
      display: flex; align-items: center; justify-content: center;
      padding: 24px;
      font-family: 'Rubik', 'Heebo', sans-serif;
    }
    .blocked-card {
      background: #fff;
      border-radius: 22px;
      padding: 30px 24px 22px;
      max-width: 400px; width: 100%;
      text-align: center;
      box-shadow: 0 30px 80px rgba(0, 0, 0, 0.40);
      direction: rtl;
      animation: blocked-pop 0.25s ease-out;
    }
    @keyframes blocked-pop {
      from { transform: scale(0.92); opacity: 0; }
      to   { transform: scale(1);    opacity: 1; }
    }
    .blocked-icon {
      width: 64px; height: 64px;
      background: linear-gradient(135deg, #d1fae5, #a7f3d0);
      border-radius: 50%;
      margin: 0 auto 16px;
      display: flex; align-items: center; justify-content: center;
      font-size: 30px;
      box-shadow: 0 8px 20px rgba(45, 168, 138, 0.25);
    }
    .blocked-title {
      margin: 0 0 10px;
      font-size: 19px; font-weight: 800;
      color: #0f172a; letter-spacing: -0.01em;
    }
    .blocked-msg {
      margin: 0 0 22px;
      font-size: 14px; color: #475569;
      line-height: 1.65;
    }
    .blocked-msg strong { color: #0f172a; }
    .blocked-primary {
      display: block; width: 100%;
      background: linear-gradient(135deg, #2DA88A, #258A72);
      color: #fff;
      padding: 13px 18px;
      border: none; border-radius: 999px;
      font-weight: 800; font-size: 15px;
      cursor: pointer; font-family: inherit;
      box-shadow: 0 8px 20px rgba(45, 168, 138, 0.35);
      transition: transform 0.12s;
    }
    .blocked-primary:hover { transform: translateY(-1px); }
    .blocked-secondary {
      display: block; width: 100%;
      margin-top: 8px;
      background: transparent; color: #64748b;
      padding: 10px; border: none;
      font-size: 13px; cursor: pointer;
      font-family: inherit;
    }
    .blocked-secondary:hover { color: #334155; }
  `;
  document.head.appendChild(style);
}
